import {
  Injectable,
  inject,
  signal,
  computed,
  effect,
  untracked,
} from '@angular/core';
import {DOCUMENT} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {environment} from '@/environments/environment';
import {fontStack, loadGoogleFonts} from '@/app/core/utils/google-font.util';
import {StorageKeys} from '@/app/core/enums/storage-keys';
import {AuthService} from '@/app/core/services/auth.service';
import {ThemeService} from '@/app/core/services/theme.service';
import {ClientTypeEnum} from '@/app/features/clients/models/client.model';
import {
  buildBrandHeroGradient,
  ensureReadable,
  pickOnPrimaryText,
} from '@/app/core/utils/color-contrast.util';
import {
  parseGeneralSettings,
  resolveAllowedClientTypes,
  type TenantGeneralSettings,
} from '../models/registration-config.model';

const SETTINGS_URL = `${environment.baseUrl}tenants/settings`;
const CLIENT_TYPES = ['client', 'tenantClient', 'Tenant Client'];
const STORAGE_KEY = 'muwarid-settings';
const DEFAULT_PRIMARY = '#0D6BB1';
const DEFAULT_SECONDARY = '#198F79';
const DEFAULT_FONT_STACK =
  "'Baloo Bhaijaan 2', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const RADIUS_PROPS = ['--radius-sm', '--radius-md', '--radius-lg'];

export interface ContactUsInfo {
  tenantId: number;
  phone: string | null;
  email: string | null;
}

export type BrandingThemeMode = 'light' | 'dark' | 'both';
export type BrandingNavAlign = 'start' | 'center' | 'end';

/** Extra tenant branding fields used by the website builder / landing page. */
export interface TenantBrandingExtras {
  favicon?: string | null;
  faviconDarkMode?: string | null;
  lightBackground?: string | null;
  darkBackground?: string | null;
  fontFamily?: string | null;
  borderRadius?: number | null;
  navAlign?: BrandingNavAlign | null;
  themeMode?: BrandingThemeMode;
  extractedColors?: string[] | null;
}

export interface TenantSettings extends TenantBrandingExtras {
  logoUrl?: string;
  logo?: string;
  logoDarkModeUrl?: string;
  logoDarkMode?: string;
  primaryColor: string;
  secondaryColor: string;
  primaryColorDarkMode?: string;
  secondaryColorDarkMode?: string;
  allowDarkMode?: boolean;
  generalSettings?: TenantGeneralSettings;
  contactUs?: ContactUsInfo;
}

/** Payload for PATCH /tenants/settings. `''` / `null` clear a value. */
export interface SaveTenantSettingsPayload {
  primaryColor?: string;
  secondaryColor?: string;
  primaryColorDarkMode?: string;
  secondaryColorDarkMode?: string;
  allowDarkMode?: boolean;
  generalSettings?: TenantGeneralSettings;
  logoFile?: File;
  removeLogo?: boolean;
  logoDarkModeFile?: File;
  removeLogoDarkMode?: boolean;
  faviconFile?: File;
  removeFavicon?: boolean;
  faviconDarkModeFile?: File;
  removeFaviconDarkMode?: boolean;
  lightBackground?: string | null;
  darkBackground?: string | null;
  fontFamily?: string | null;
  borderRadius?: number | null;
  navAlign?: BrandingNavAlign | null;
  themeMode?: BrandingThemeMode;
}

/** The BE falls back to this file when the tenant has no logo. */
export function isDefaultTenantLogo(url: string | null | undefined): boolean {
  return !!url && url.includes('default_logo');
}

function pickBrandingExtras(src: TenantBrandingExtras): TenantBrandingExtras {
  return {
    favicon: src.favicon ?? null,
    faviconDarkMode: src.faviconDarkMode ?? null,
    lightBackground: src.lightBackground ?? null,
    darkBackground: src.darkBackground ?? null,
    fontFamily: src.fontFamily ?? null,
    borderRadius: src.borderRadius ?? null,
    navAlign: src.navAlign ?? null,
    themeMode: src.themeMode ?? 'light',
    extractedColors: src.extractedColors ?? [],
  };
}

@Injectable({providedIn: 'root'})
export class SettingsService {
  private document = inject(DOCUMENT);
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private themeService = inject(ThemeService);

  private allowDarkModeSignal = signal<boolean>(true);
  readonly allowDarkMode = computed(() => this.allowDarkModeSignal());

  private settingsVersionSignal = signal<number>(0);

  // Reactive computed settings that updates when settingsVersionSignal changes
  readonly settings = computed(() => {
    // Force re-computation by accessing the version signal
    this.settingsVersionSignal();
    return this.getSettings();
  });

  constructor() {
    this.hydrateAllowDarkModeFromCache();

    // Register callback with ThemeService to apply colors on theme change
    this.themeService.setThemeChangeCallback(() => {
      this.applyCurrentThemeColors();
    });

    // Login / logout / context switch without a reload: refetch and reapply
    // branding for the new user (init() alone only runs at app start).
    let lastUserKey: string | null | undefined;
    effect(() => {
      const user = this.auth.userData();
      const key = user?.token ? `${user.user?.type}:${user.user?.id}` : null;
      if (key === lastUserKey) return;
      const firstRun = lastUserKey === undefined;
      lastUserKey = key;
      if (firstRun) return; // app.ts calls init() on start
      untracked(() => {
        if (key) this.init();
        else this.resetThemeColors();
      });
    });

    // Reactive effect: reapply colors when theme or settings change
    effect(() => {
      const theme = this.themeService.current();
      const version = this.settingsVersionSignal();
      if (version > 0) {
        this.applyCurrentThemeColors();
      }
    });
  }

  private hydrateAllowDarkModeFromCache(): void {
    const cached = this.getSettings();
    if (localStorage.getItem(STORAGE_KEY)) {
      this.allowDarkModeSignal.set(cached.allowDarkMode ?? true);
      // Increment version so computed signals react to cached settings
      this.settingsVersionSignal.update((v) => v + 1);
      if (this.isClient()) {
        this.enforceThemeRestriction();
      }
    }
  }

  private isClient(): boolean {
    const type = this.auth.userData()?.user?.type ?? '';
    return CLIENT_TYPES.includes(type);
  }

  private enforceThemeRestriction(): void {
    if (!this.isClient()) return;
    if (!this.allowDarkModeSignal() && this.themeService.current() === 'dark') {
      this.themeService.change('light');
    }
  }

  getSettings(): TenantSettings {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as TenantSettings & {
          allowedClientTypes?: ClientTypeEnum[];
          allowWathq?: boolean;
        };
        const primaryColor = parsed.primaryColor || DEFAULT_PRIMARY;
        const secondaryColor = parsed.secondaryColor || DEFAULT_SECONDARY;
        const generalSettings =
          parseGeneralSettings(parsed.generalSettings) ??
          ({
            allowedClientTypes: parsed.allowedClientTypes,
            wathqVerificationEnabled: parsed.allowWathq,
          } as TenantGeneralSettings);
        return {
          primaryColor,
          secondaryColor,
          primaryColorDarkMode: parsed.primaryColorDarkMode || primaryColor,
          secondaryColorDarkMode:
            parsed.secondaryColorDarkMode || secondaryColor,
          logoUrl: parsed.logoUrl ?? parsed.logo,
          logoDarkModeUrl: parsed.logoDarkModeUrl ?? parsed.logoDarkMode,
          allowDarkMode: parsed.allowDarkMode ?? true,
          ...pickBrandingExtras(parsed),
          generalSettings: {
            allowedClientTypes: resolveAllowedClientTypes(
              generalSettings.allowedClientTypes,
            ),
            wathqVerificationEnabled:
              generalSettings.wathqVerificationEnabled ?? true,
          },
          contactUs: parsed.contactUs,
        };
      }
    } catch {}
    return {
      primaryColor: DEFAULT_PRIMARY,
      secondaryColor: DEFAULT_SECONDARY,
      primaryColorDarkMode: DEFAULT_PRIMARY,
      secondaryColorDarkMode: DEFAULT_SECONDARY,
      allowDarkMode: true,
      generalSettings: {
        allowedClientTypes: resolveAllowedClientTypes(),
        wathqVerificationEnabled: true,
      },
    };
  }

  private cacheSettings(settings: TenantSettings): void {
    const primaryColor = settings.primaryColor || DEFAULT_PRIMARY;
    const secondaryColor = settings.secondaryColor || DEFAULT_SECONDARY;
    const toStore = {
      primaryColor,
      secondaryColor,
      primaryColorDarkMode: settings.primaryColorDarkMode || primaryColor,
      secondaryColorDarkMode: settings.secondaryColorDarkMode || secondaryColor,
      logoUrl: settings.logo ?? settings.logoUrl,
      logoDarkModeUrl: settings.logoDarkMode ?? settings.logoDarkModeUrl,
      allowDarkMode: settings.allowDarkMode ?? true,
      ...pickBrandingExtras(settings),
      generalSettings: {
        allowedClientTypes: resolveAllowedClientTypes(
          settings.generalSettings?.allowedClientTypes,
        ),
        wathqVerificationEnabled:
          settings.generalSettings?.wathqVerificationEnabled ?? true,
      },
      contactUs: settings.contactUs,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  }

  private normalizeSettings(res: TenantSettings): TenantSettings {
    const primaryColor = res.primaryColor || DEFAULT_PRIMARY;
    const secondaryColor = res.secondaryColor || DEFAULT_SECONDARY;
    const general =
      parseGeneralSettings(res.generalSettings) ??
      ({
        allowedClientTypes: (res as {allowedClientTypes?: ClientTypeEnum[]})
          .allowedClientTypes,
        wathqVerificationEnabled: (res as {allowWathq?: boolean}).allowWathq,
      } as TenantGeneralSettings);
    return {
      primaryColor,
      secondaryColor,
      primaryColorDarkMode: res.primaryColorDarkMode || primaryColor,
      secondaryColorDarkMode: res.secondaryColorDarkMode || secondaryColor,
      logoUrl: res.logo ?? res.logoUrl,
      logoDarkModeUrl: res.logoDarkMode ?? res.logoDarkModeUrl,
      allowDarkMode: res.allowDarkMode ?? true,
      ...pickBrandingExtras(res),
      generalSettings: {
        allowedClientTypes: resolveAllowedClientTypes(
          general.allowedClientTypes,
        ),
        wathqVerificationEnabled: general.wathqVerificationEnabled ?? true,
      },
      contactUs: res.contactUs,
    };
  }

  private _fetchInFlight: Promise<TenantSettings | null> | null = null;

  fetchFromApi(): Promise<TenantSettings | null> {
    if (this._fetchInFlight) return this._fetchInFlight;
    this._fetchInFlight = firstValueFrom(
      this.http.get<TenantSettings>(SETTINGS_URL),
    )
      .then((res) => {
        const settings = this.normalizeSettings(res);
        this.cacheSettings(settings);
        this.allowDarkModeSignal.set(settings.allowDarkMode ?? true);
        this.enforceThemeRestriction();
        this.settingsVersionSignal.update((v) => v + 1);
        return settings;
      })
      .catch(() => null)
      .finally(() => {
        this._fetchInFlight = null;
      });
    return this._fetchInFlight;
  }

  saveSettings(
    settings: Partial<TenantSettings> & {logoFile?: File; removeLogo?: boolean},
  ): void {
    const current = this.getSettings();
    const merged = {...current, ...settings};
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  }

  saveToApi(settings: SaveTenantSettingsPayload): Promise<TenantSettings> {
    const wasAllowed = this.allowDarkModeSignal();
    const form = new FormData();

    // Only append fields that are provided (changed fields)
    if (settings.primaryColor !== undefined) {
      form.append('primaryColor', settings.primaryColor);
    }
    if (settings.secondaryColor !== undefined) {
      form.append('secondaryColor', settings.secondaryColor);
    }
    // An empty dark color clears it (BE falls back to the light color)
    if (settings.primaryColorDarkMode !== undefined) {
      form.append('primaryColorDarkMode', settings.primaryColorDarkMode);
    }
    if (settings.secondaryColorDarkMode !== undefined) {
      form.append('secondaryColorDarkMode', settings.secondaryColorDarkMode);
    }
    if (settings.allowDarkMode !== undefined) {
      form.append('allowDarkMode', String(settings.allowDarkMode));
    }
    if (settings.generalSettings) {
      form.append('generalSettings', JSON.stringify(settings.generalSettings));
    }
    if (settings.removeLogo) {
      form.append('removeLogo', 'true');
    } else if (settings.logoFile) {
      form.append('logo', settings.logoFile);
    }
    if (settings.removeLogoDarkMode) {
      form.append('removeLogoDarkMode', 'true');
    } else if (settings.logoDarkModeFile) {
      form.append('logoDarkMode', settings.logoDarkModeFile);
    }
    if (settings.removeFavicon) {
      form.append('removeFavicon', 'true');
    } else if (settings.faviconFile) {
      form.append('favicon', settings.faviconFile);
    }
    if (settings.removeFaviconDarkMode) {
      form.append('removeFaviconDarkMode', 'true');
    } else if (settings.faviconDarkModeFile) {
      form.append('faviconDarkMode', settings.faviconDarkModeFile);
    }
    // `null` / '' is sent as '' so the BE clears the value
    const clearable = [
      'lightBackground',
      'darkBackground',
      'fontFamily',
      'borderRadius',
      'navAlign',
      'themeMode',
    ] as const;
    for (const key of clearable) {
      const value = settings[key];
      if (value !== undefined)
        form.append(key, value === null ? '' : String(value));
    }

    return firstValueFrom(
      this.http.patch<TenantSettings>(SETTINGS_URL, form),
    ).then((res) => {
      const s = this.normalizeSettings(res);
      this.cacheSettings(s);
      const nowAllowed = s.allowDarkMode ?? true;
      this.allowDarkModeSignal.set(nowAllowed);
      if (wasAllowed && !nowAllowed) {
        this.enforceThemeRestriction();
      }
      this.settingsVersionSignal.update((v) => v + 1);
      return s;
    });
  }
  applyCurrentThemeColors(options?: {forceLight?: boolean}): void {
    if (!this.isClient()) {
      this.resetThemeColors();
      return;
    }

    const settings = this.getSettings();
    // TODO: This for excluding the landing builder from the dark mode, and we will remove it later.
    const isDark = options?.forceLight
      ? false
      : this.themeService.current() === 'dark';

    const primary = isDark
      ? settings.primaryColorDarkMode || settings.primaryColor
      : settings.primaryColor;
    const secondary = isDark
      ? settings.secondaryColorDarkMode || settings.secondaryColor
      : settings.secondaryColor;

    const root = this.document.documentElement.style;

    root.setProperty('--color-primary', primary);
    root.setProperty('--brand-primary', primary);
    root.setProperty(
      '--brand-primary-hover',
      `color-mix(in srgb, ${primary} 85%, ${isDark ? 'white' : 'black'})`,
    );
    root.setProperty(
      '--brand-primary-light',
      `color-mix(in srgb, ${primary} ${isDark ? '20%' : '12%'}, transparent)`,
    );
    root.setProperty(
      '--bg-hover',
      `color-mix(in srgb, ${primary} 8%, transparent)`,
    );

    root.setProperty('--color-secondary', secondary);
    root.setProperty('--brand-secondary', secondary);

    const surfaceRef = getComputedStyle(this.document.documentElement)
      .getPropertyValue('--color-surface')
      .trim();
    root.setProperty(
      '--brand-primary-text',
      ensureReadable(primary, surfaceRef),
    );
    root.setProperty(
      '--brand-secondary-text',
      ensureReadable(secondary, surfaceRef),
    );

    root.setProperty('--color-text-on-primary', pickOnPrimaryText(primary));

    const hero = buildBrandHeroGradient(primary);
    root.setProperty('--brand-primary-hero-from', hero.from);
    root.setProperty('--brand-primary-hero-to', hero.to);

    this.applyBrandingExtras(settings, isDark);
  }

  /** Tenant background, font, radius and favicon for the client app. */
  private applyBrandingExtras(settings: TenantSettings, isDark: boolean): void {
    const root = this.document.documentElement.style;
    const background = isDark
      ? settings.darkBackground
      : settings.lightBackground;
    if (background) root.setProperty('--color-bg', background);
    else root.removeProperty('--color-bg');

    if (settings.fontFamily) {
      loadGoogleFonts(this.document, settings.fontFamily);
      const font = fontStack(settings.fontFamily, DEFAULT_FONT_STACK);
      root.setProperty('--font-body', font);
      root.setProperty('--font-heading', font);
    } else {
      root.removeProperty('--font-body');
      root.removeProperty('--font-heading');
    }

    // 0-100 → 0-24px, same scale as the public website
    if (settings.borderRadius != null) {
      const px = Math.round((settings.borderRadius / 100) * 24);
      root.setProperty('--radius-sm', `${Math.round(px * 0.8)}px`);
      root.setProperty('--radius-md', `${px}px`);
      root.setProperty('--radius-lg', `${Math.round(px * 1.4)}px`);
    } else {
      for (const p of RADIUS_PROPS) root.removeProperty(p);
    }

    const favicon =
      (isDark ? settings.faviconDarkMode : null) || settings.favicon;
    if (favicon) this.setFavicon(favicon);
    else this.restoreFavicons();
  }

  private originalFavicons: {link: HTMLLinkElement; href: string}[] | null =
    null;
  private setFavicon(url: string): void {
    const links = Array.from(
      this.document.querySelectorAll<HTMLLinkElement>("link[rel*='icon']"),
    );
    this.originalFavicons ??= links.map((link) => ({link, href: link.href}));
    for (const link of links) {
      link.href = url;
      link.removeAttribute('type');
    }
  }

  private restoreFavicons(): void {
    for (const {link, href} of this.originalFavicons ?? []) link.href = href;
    this.originalFavicons = null;
  }

  private resetThemeColors(): void {
    const root = this.document.documentElement.style;
    for (const prop of [
      '--color-primary',
      '--brand-primary',
      '--brand-primary-text',
      '--brand-primary-hover',
      '--brand-primary-light',
      '--bg-hover',
      '--color-secondary',
      '--brand-secondary',
      '--brand-secondary-text',
      '--color-text-on-primary',
      '--brand-primary-hero-from',
      '--brand-primary-hero-to',
      '--color-bg',
      '--font-body',
      '--font-heading',
      ...RADIUS_PROPS,
    ]) {
      root.removeProperty(prop);
    }
    this.restoreFavicons();
  }

  init(): void {
    if (!this.hasAuth()) return;
    if (this.isClient()) {
      this.fetchFromApi().then(() => this.applyCurrentThemeColors());
      return;
    }
    this.resetThemeColors();
    this.fetchFromApi();
  }

  private hasAuth(): boolean {
    try {
      const raw =
        localStorage.getItem(StorageKeys.USER_DATA) ??
        sessionStorage.getItem(StorageKeys.USER_DATA);
      if (raw) {
        const parsed = JSON.parse(raw);
        return !!parsed?.token;
      }
    } catch {}
    return false;
  }
}
